import type { ReactNode } from "react";
import { ArrowRight, Check, Code2, Layers, Shield } from "lucide-react";
import { Link } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { DiscoveryForm } from "@/components/marketing/DiscoveryForm";
import { WordpressConnectionTest } from "@/components/wordpress/WordpressConnectionTest";

function ApproachCard({
  title,
  icon,
  pros,
  bestFor,
}: {
  title: string;
  icon: ReactNode;
  pros: string[];
  bestFor: string;
}) {
  return (
    <Card className="h-full">
      <CardHeader className="space-y-2">
        <div className="flex items-center gap-3">
          <div className="grid h-10 w-10 place-items-center rounded-xl bg-primary/10 text-primary ring-1 ring-primary/20">
            {icon}
          </div>
          <CardTitle className="text-base font-semibold tracking-tight">
            {title}
          </CardTitle>
        </div>
        <CardDescription className="text-sm leading-relaxed">
          En iyi kullanım: <span className="font-medium">{bestFor}</span>
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid gap-2">
          {pros.map((item) => (
            <div key={item} className="flex items-start gap-2 text-sm">
              <Check className="mt-0.5 h-4 w-4 text-primary" />
              <span className="text-muted-foreground">{item}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export default function WordpressBaglanti() {
  return (
    <div className="space-y-10">
      <section className="rounded-3xl border bg-card p-7 shadow-sm sm:p-10">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="secondary" className="rounded-full">
                Bağlantı & Yol Haritası
              </Badge>
              <Badge variant="outline" className="rounded-full">
                33n.org
              </Badge>
            </div>
            <h1 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">
              WordPress’i buraya nasıl bağlayabiliriz?
            </h1>
            <p className="max-w-2xl text-sm leading-relaxed text-muted-foreground">
              Elementor ile tasarlanmış mevcut siteniz var. Hedefimiz; var olanı
              çöpe atmadan, plugin ihtiyaçlarını destekleyecek şekilde yeni bir
              yapı kurmak. Aşağıdaki 3 yöntem arasından birini seçerek ilerlemek
              en sağlıklısı.
            </p>
          </div>

          <div className="flex shrink-0 flex-col gap-3">
            <Button asChild className="rounded-full">
              <Link to="/#kisa-form">
                Ana sayfadaki forma git <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
            <Button asChild variant="outline" className="rounded-full">
              <Link to="#kisa-form">Bu sayfadaki form</Link>
            </Button>
          </div>
        </div>
      </section>

      <section id="yaklasimlar" className="space-y-4">
        <div className="space-y-2">
          <h2 className="font-display text-2xl font-semibold tracking-tight">
            3 bağlantı yaklaşımı
          </h2>
          <p className="max-w-3xl text-sm leading-relaxed text-muted-foreground">
            Seçim; içerik yönetimi, SEO, hız ve plugin geliştirme maliyetini
            belirler. İsterseniz ilk sprintte “hibrit” başlar, sonra tamamen
            headless’a geçeriz.
          </p>
        </div>

        <div className="grid gap-4 lg:grid-cols-3">
          <ApproachCard
            title="Klasik WordPress + embed"
            icon={<Layers className="h-5 w-5" />}
            bestFor="Hızlı başlangıç, minimum risk"
            pros={[
              "Mevcut Elementor sayfaları aynen kalır",
              "Bu uygulamayı belirli sayfalara widget/iframe olarak ekleriz",
              "Plugin akışlarını adım adım iyileştiririz",
            ]}
          />
          <ApproachCard
            title="Headless WordPress (REST/GraphQL)"
            icon={<Code2 className="h-5 w-5" />}
            bestFor="Performans, modern UI ve tam kontrol"
            pros={[
              "WordPress sadece içerik yönetimi olur",
              "Burada pixel-perfect tasarım + dönüşüm odaklı akışlar kurarız",
              "Plugin mantığını API/özel endpoint’e taşıyabiliriz",
            ]}
          />
          <ApproachCard
            title="Tam yeniden kurgulama"
            icon={<Shield className="h-5 w-5" />}
            bestFor="Uzun vadeli ürünleşme"
            pros={[
              "Üyelik/ödeme/rapor sistemi için net bir mimari kurulur",
              "Tasarım, teklif ve analytics en baştan planlanır",
              "Plugin’in yaptığı işler modern servisler ile yeniden yazılır",
            ]}
          />
        </div>
      </section>

      <section className="rounded-3xl border bg-card p-7 shadow-sm sm:p-10">
        <div className="grid gap-8 lg:grid-cols-2 lg:items-start">
          <div className="space-y-4">
            <h2 className="font-display text-2xl font-semibold tracking-tight">
              Plugin için netleştireceğimiz şeyler
            </h2>
            <p className="text-sm leading-relaxed text-muted-foreground">
              “Plugin ile ilgili eklemek istediklerim var” dediğiniz kısım için;
              önce iş kurallarını çıkarırız. Sonra bu kuralları ya WordPress
              içinde (custom plugin) ya da burada (API) yönetiriz.
            </p>

            <div className="rounded-2xl border bg-background p-5">
              <div className="text-sm font-medium">Örnek sorular</div>
              <Separator className="my-3" />
              <div className="grid gap-2 text-sm text-muted-foreground">
                {[
                  "Numeroloji hesaplaması nerede çalışacak?",
                  "Rapor PDF mi, ekran mı, e-posta mı?",
                  "Ödeme sonrası teslimat kuralı nedir?",
                  "Üyelik seviyeleri hangi içerikleri açacak?",
                  "Hangi sayfalarda upsell/çapraz satış olacak?",
                ].map((item) => (
                  <div key={item}>• {item}</div>
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row">
              <Button asChild className="rounded-full">
                <Link to="/">Ana sayfaya dön</Link>
              </Button>
              <Button asChild variant="outline" className="rounded-full">
                <Link to="#kisa-form">Formu doldur</Link>
              </Button>
            </div>
          </div>

          <div className="space-y-4">
            <div id="kisa-form" className="rounded-2xl border bg-background p-5">
              <div className="mb-4 space-y-1">
                <div className="font-display text-xl font-semibold tracking-tight">
                  Kısa form
                </div>
                <div className="text-sm text-muted-foreground">
                  Buraya yazdıklarınız, tasarım ve entegrasyon kararlarını
                  hızlandırır.
                </div>
              </div>
              <DiscoveryForm defaultSiteUrl="33n.org" />
            </div>

            <WordpressConnectionTest />
          </div>
        </div>
      </section>
    </div>
  );
}
